import React, { useRef } from 'react'
import { differenceInYears, format } from 'date-fns'
import styled from 'styled-components'
import { parseDateTime } from '~/prix/types/dateTime'
import { maskNumbers, masks } from '~/prix/utils/types/mask'
import CopyIcon from '~/meta/copy.svg'
import { NativeBaseOptional } from '~/prix/types/type'
import { institutionalColors } from '~/design'

const DataFormContainer = styled.div<{ $useWideSideMarginOnTablet: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px;
  align-content: flex-start;

  div:has(input, textarea) {
    width: 100%;
    height: fit-content;
  }

  span {
    color: #005eb8;
    font-size: 1.10345rem;
    font-weight: 400;
    line-height: 1.6069rem;
  }

  input,
  textarea {
    width: 100%;
    border-radius: 4px;
    border: 1px solid ${institutionalColors.graySebrae75};
    background: #fff;
    padding: 5px;

    color: #394d60;
    font-size: 1.1rem;
    font-weight: 400;
    line-height: 1.6rem;

    min-width: 150px;

    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:focus {
      outline: none;
    }
  }

  .half {
    width: calc(50% - 8px);
  }

  .two-third {
    width: 66%;
  }

  .third {
    width: calc(33% - 9px);
  }

  @media (max-width: 1421px) {
    .two-third {
      width: 100%;
    }

    .third {
      width: 48%;
    }
  }

  @media (max-width: 1290px) {
    .third {
      width: calc(50% - 8px);
    }
  }

  @media (max-width: 1200px) {
    margin: ${({ $useWideSideMarginOnTablet }) => ($useWideSideMarginOnTablet ? '0 32px' : '0')};
    .third {
      width: calc(50% - 8px);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    .half {
      width: 100%;
    }

    .two-third {
      width: 100%;
    }

    .third {
      width: 100%;
    }

    padding: 12px 0 0;
  }
`

const WrapperInput = styled.div`
  display: inline-flex;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    border: none;
    background: transparent;
    outline: none;
    cursor: pointer;
    margin-left: -22px;
    margin-top: 2px;
    width: fit-content;
    height: fit-content;
  }
`

interface Items {
  city: string
  cnpj: string
  complement: string
  corporateName: string
  email: string
  neighborhood: string
  number: number
  openingDate: string
  phoneNumberOne: string
  phoneNumberOneAreaCode: string
  phoneNumberTwo: string
  phoneNumberTwoAreaCode: string
  pointOnStreet: [longitude: number, latitude: number] | null
  pointOnStreetDirectionAngle: number | null
  postalCode: string
  publicPlace: string
  sector: string
  ageGroup?: string
  sizeAbbreviation?: string
  size: string
  state: string
  tradeName: string
}
;[]

interface CnaeProps {
  cnae_id: NativeBaseOptional
  description: NativeBaseOptional
  is_main: NativeBaseOptional
  segmentDescription: any
}

// export const sectorAux = {
//   agribusiness: 'Agronegócios',
//   commerce: 'Comércio',
//   industry: 'Indústria',
//   service: 'Serviços',
// }

export const sizeAux = {
  unknown: 'Não informado',
  MEI: 'Microempreendedor Individual (MEI)',
  ME: 'Microempresa (ME)',
  EPP: 'Empresa de Pequeno Porte (EPP)',
  micro: 'Microempresa (ME)',
  small: 'Empresa de Pequeno Porte (EPP)',
  other: 'Demais',
}

export const ageGroupAux = {
  startup: 'Startup (0 a 2 anos)',
  young: 'Jovem (2 a 5 anos)',
  growth: 'Crescimento (5 a 10 anos)',
  established: 'Estabelecida (mais de 10 anos)',
}

export default function DataForm({
  item,
  cnae,
  readOnly = false,
  useWideSideMarginOnTablet = true,
}: {
  item: Items | any
  cnae?: CnaeProps[]
  readOnly?: boolean
  useWideSideMarginOnTablet?: boolean
}) {
  const mainCnae = cnae?.[0]?.cnae_id
    ? `${cnae[0]?.cnae_id} - ${cnae[0]?.description}`
    : 'Desconhecido'
  const cnpj = item?.cnpj ? maskNumbers(item.cnpj.toString(), masks.cnpj) : 'Desconhecido'
  const openingDate = item?.openingDate
    ? format(parseDateTime(item.openingDate), 'dd/MM/yyyy')
    : 'Desconhecido'

  // Não sendo usado por enquanto
  // const sector = item?.sector ? sectorAux[item?.sector as unknown as keyof typeof sectorAux] : 'Desconhecido'
  const sizeKey = item?.sizeAbbreviation ?? item?.size
  const size = sizeKey
    ? (sizeAux[sizeKey as keyof typeof sizeAux] ?? 'Desconhecido')
    : 'Desconhecido'
  const ageGroup = item?.openingDate
    ? (() => {
        const years = differenceInYears(new Date(), parseDateTime(item.openingDate))

        if (years <= 2) {
          return ageGroupAux.startup
        }
        if (years <= 5) {
          return ageGroupAux.young
        }
        if (years <= 10) {
          return ageGroupAux.growth
        }
        return ageGroupAux.established
      })()
    : 'Desconhecido'
  const mainTelephone =
    item?.phoneNumberOneAreaCode && item?.phoneNumberOne
      ? maskNumbers(
          item?.phoneNumberOneAreaCode.toString() + item?.phoneNumberOne.toString(),
          masks.phone,
        )
      : 'Desconhecido'
  const secondaryTelephone =
    item?.phoneNumberTwoAreaCode && item?.phoneNumberTwo
      ? maskNumbers(
          item?.phoneNumberTwoAreaCode.toString() + item?.phoneNumberTwo.toString(),
          masks.phone,
        )
      : 'Desconhecido'
  const hasSecondaryTelephone = secondaryTelephone !== 'Desconhecido'
  const postalCode = item?.postalCode
    ? maskNumbers(item.postalCode.toString(), masks.cep)
    : 'Desconhecido'
  const formattedAddressParts = [item?.publicPlace, item?.number, item?.complement]
  const formattedAddress = formattedAddressParts.filter(Boolean).join(', ')
  const segment = cnae?.[0]?.segmentDescription ?? 'Desconhecido'

  const inputRefs = useRef<(HTMLInputElement | any)[]>([])

  const handleCopy = (index: number) => {
    const value = inputRefs.current[index]?.value || ''
    navigator.clipboard
      .writeText(value)
      .then(() => {
        inputRefs.current[index]!.value = 'Copiado!'
        setTimeout(() => {
          if (inputRefs.current[index]) {
            inputRefs!.current[index]!.value = value
          }
        }, 2000)
      })
      .catch(error => {
        console.error('Failed to copy text: ', error)
      })
  }

  return (
    <DataFormContainer $useWideSideMarginOnTablet={useWideSideMarginOnTablet}>
      <div>
        <span>Razão social</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[0] = el)}
            value={item?.corporateName || 'Desconhecido'}
          />
          <button onClick={() => handleCopy(0)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className={hasSecondaryTelephone ? 'third' : 'half'}>
        <span>CNPJ</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[1] = el)}
            value={cnpj}
          />
          <button onClick={() => handleCopy(1)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className={hasSecondaryTelephone ? 'third' : 'half'}>
        <span>Telefone principal</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[2] = el)}
            value={mainTelephone}
          />
          <button onClick={() => handleCopy(2)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      {hasSecondaryTelephone && (
        <div className='third'>
          <span>Telefone secundário</span>
          <WrapperInput>
            <input
              type='text'
              readOnly={readOnly}
              ref={el => (inputRefs.current[3] = el)}
              value={secondaryTelephone}
            />
            <button onClick={() => handleCopy(3)}>
              <CopyIcon width={13} height={13} />
            </button>
          </WrapperInput>
        </div>
      )}
      <div>
        <span>Email</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[4] = el)}
            value={item?.email || 'Desconhecido'}
          />
          <button onClick={() => handleCopy(4)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className='half'>
        <span>Data de abertura</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[6] = el)}
            value={openingDate}
          />
          <button onClick={() => handleCopy(6)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className='half'>
        <span>Maturidade</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[15] = el)}
            value={ageGroup}
          />
          <button onClick={() => handleCopy(15)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div>
        <span>CNAE principal</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[7] = el)}
            value={mainCnae}
          />
          <button onClick={() => handleCopy(7)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className='half'>
        <span>Segmento</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[8] = el)}
            value={segment}
          />
          <button onClick={() => handleCopy(8)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className='half'>
        <span>Porte</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[10] = el)}
            value={size}
          />
          <button onClick={() => handleCopy(10)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      {/* <div>
        <span>Setor</span>
        <WrapperInput>
          <input type="text" readOnly={readOnly} ref={(el) => inputRefs.current[9] = el}
            value={sector} />
          <button onClick={() => handleCopy(9)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div> */}

      <div>
        <span>Endereço</span>
        <WrapperInput>
          <textarea
            rows={2}
            readOnly={readOnly}
            ref={el => (inputRefs.current[5] = el)}
            value={formattedAddress}
          />
          <button onClick={() => handleCopy(5)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>

      <div className='half'>
        <span>Bairro</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[11] = el)}
            value={item?.neighborhood || 'Desconhecido'}
          />
          <button onClick={() => handleCopy(11)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className='half'>
        <span>Cidade</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[12] = el)}
            value={item?.city || 'Desconhecido'}
          />
          <button onClick={() => handleCopy(12)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className='half'>
        <span>CEP</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[14] = el)}
            value={postalCode}
          />
          <button onClick={() => handleCopy(14)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
      <div className='half'>
        <span>Estado</span>
        <WrapperInput>
          <input
            type='text'
            readOnly={readOnly}
            ref={el => (inputRefs.current[13] = el)}
            value={item?.state || 'Desconhecido'}
          />
          <button onClick={() => handleCopy(13)}>
            <CopyIcon width={13} height={13} />
          </button>
        </WrapperInput>
      </div>
    </DataFormContainer>
  )
}
